import {
  SignalDisplayMode,
  SignalSlot,
  SignalStatus,
  SignalsDefinition,
  signalsBaseTopic,
  signalsSignalTopic,
} from './types';
import { DashboardTopicValue } from '../types';

export interface ResolvedSlotStatus {
  slotId: string;
  slotName: string;
  statusId: string | null;
  name: string;
  color: string;
  mode: SignalDisplayMode;
  known: boolean;
}

const OFF: Pick<SignalStatus, 'name' | 'color' | 'mode'> = {
  name: '—',
  color: '#000000',
  mode: 'off',
};

/** Match MQTT atomic payload to a status by name (preferred) or id. */
export function findStatusByPayload(slot: SignalSlot, payload: string | null | undefined): SignalStatus | undefined {
  const token = String(payload || '').trim();
  if (!token) return undefined;
  const byName = slot.statuses.find((s) => s.name.toLowerCase() === token.toLowerCase());
  if (byName) return byName;
  return slot.statuses.find((s) => s.id === token);
}

export function resolveSlotLiveStatus(
  slot: SignalSlot,
  payload: string | null | undefined,
  opts?: { preferPreviewWhenEmpty?: boolean },
): ResolvedSlotStatus {
  const token = payload != null && String(payload).trim() !== '' ? String(payload).trim() : null;
  const found = token ? findStatusByPayload(slot, token) : undefined;
  if (found) {
    return {
      slotId: slot.id || slot.name,
      slotName: slot.name,
      statusId: found.id,
      name: found.name,
      color: found.color,
      mode: found.mode,
      known: true,
    };
  }
  if (token == null && opts?.preferPreviewWhenEmpty !== false) {
    const previewId = slot.previewStatus || slot.statuses[0]?.id;
    const preview = slot.statuses.find((s) => s.id === previewId) || slot.statuses[0];
    if (preview) {
      return {
        slotId: slot.id || slot.name,
        slotName: slot.name,
        statusId: preview.id,
        name: preview.name,
        color: preview.color,
        mode: preview.mode,
        known: false,
      };
    }
  }
  return {
    slotId: slot.id || slot.name,
    slotName: slot.name,
    statusId: token,
    name: token || OFF.name,
    color: OFF.color,
    mode: OFF.mode,
    known: false,
  };
}

function mqttPayloadToken(value: DashboardTopicValue | null | undefined): string | null {
  if (!value) return null;
  if (typeof value.statusRaw === 'string' && value.statusRaw.trim()) return value.statusRaw.trim();
  if (Array.isArray(value.statuses) && value.statuses[0]) return String(value.statuses[0]).trim();
  if (typeof value.raw === 'string' && value.raw.trim() && !value.raw.trim().startsWith('{')) {
    return value.raw.trim();
  }
  return null;
}

/**
 * Resolve live slot display from per-signal MQTT topics:
 * `{base}/{SignalName}` → atomic status name (e.g. `rot`).
 */
export function resolveLiveStatusesFromMqtt(
  def: SignalsDefinition | null | undefined,
  values: Record<string, DashboardTopicValue | null | undefined> | null | undefined,
  opts?: { preferPreviewWhenEmpty?: boolean; baseTopic?: string },
): ResolvedSlotStatus[] {
  const signals = def?.signals || [];
  const base = signalsBaseTopic(opts?.baseTopic || def);
  const preferPreview = opts?.preferPreviewWhenEmpty !== false;
  const map = values || {};

  return signals.map((slot) => {
    const topic = signalsSignalTopic(base, slot.name);
    const token = topic ? mqttPayloadToken(map[topic]) : null;
    if (token == null) {
      // Once any signal has live MQTT, empty siblings show off — not preview.
      const anyLive = signals.some((s) => {
        const t = signalsSignalTopic(base, s.name);
        return Boolean(t && mqttPayloadToken(map[t]));
      });
      return resolveSlotLiveStatus(slot, null, {
        preferPreviewWhenEmpty: preferPreview && !anyLive,
      });
    }
    return resolveSlotLiveStatus(slot, token, { preferPreviewWhenEmpty: false });
  });
}

/** @deprecated Prefer resolveLiveStatusesFromMqtt — kept for preview editor CSV-less use. */
export function resolveLiveStatuses(
  def: SignalsDefinition | null | undefined,
  statusCsvOrList: string | string[] | null | undefined,
  opts?: { preferPreviewWhenEmpty?: boolean },
): ResolvedSlotStatus[] {
  const signals = def?.signals || [];
  if (statusCsvOrList == null || statusCsvOrList === '') {
    return signals.map((slot) => resolveSlotLiveStatus(slot, null, opts));
  }
  // Legacy: single string applied to all, or CSV by index, or one token per list entry.
  const tokens = Array.isArray(statusCsvOrList)
    ? statusCsvOrList
    : String(statusCsvOrList).includes(',')
      ? String(statusCsvOrList).split(',').map((s) => s.trim())
      : [String(statusCsvOrList).trim()];
  const hasLive = tokens.some((t) => t);
  if (!hasLive) {
    return signals.map((slot) => resolveSlotLiveStatus(slot, null, opts));
  }
  return signals.map((slot, i) => resolveSlotLiveStatus(slot, tokens[i] ?? '', { preferPreviewWhenEmpty: false }));
}
